import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Tooltip } from "primereact/tooltip";
import { Column } from "primereact/column";
import {
  Dropdown,
  DropdownChangeEvent,
  DropdownProps,
} from "primereact/dropdown";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Nullable } from "primereact/ts-helpers";
import { Dialog } from "primereact/dialog";

import NoDataCreated from "../../Components/NoDataCreated";
import ErrorPage from "../../Components/ErrorPage";
import PageLoader from "../../Components/PageLoader";
import DatePicker from "../../Components/DatePicker";
import Textarea from "../../Components/TextArea";
import TextInput from "../../Components/TextInput";
import { goToTop } from "../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../Utils/getDate";
import {
  taskPartCreateApi,
  taskPartDeleteApi,
  taskPartGetDataApi,
  taskPartStatusChangedApi,
  taskPartUpdateApi,
  taskProjectGetDataApi,
} from "../../Api/Task/TaskApi";
import {
  Assign,
  AssignContact,
  CreateTaskArray,
  Projects,
  Tasks,
} from "../../Controllers/Types/Task/TaskInterface";
import { baseURL } from "../../Api/ApiConstant";
import { checkNumber, checkText } from "../../Utils/Validator";
import { useAppDispatch, useAppSelector } from "../../Controllers/hooks";
import { ContactSubtype } from "../../Utils/appConstant";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";
import NumberInput from "../../Components/NumberInput";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { fetchAllContact } from "../../Controllers/Thunks/contactThunks";
import { setStatus } from "../../Controllers/Slices/contactSlice";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";

interface Status {
  label: string;
  value: number;
}

interface Priority {
  name: string;
  colorCode: string;
}

const priorities: Priority[] = [
  {
    name: "High",
    colorCode: "#EF9A9A",
  },
  {
    name: "Medium",
    colorCode: "#FFEFBC",
  },
  {
    name: "Low",
    colorCode: "#B9F6CA",
  },
];

const Task: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const error = "";

  const today = new Date();
  const toast = useRef<Toast>(null);
  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  const [tasksData, setTasksData] = useState<Tasks[]>([]);
  const [filteredTasksData, setFilteredTasksData] = useState<Tasks[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<Priority | null>(null);
  const [assignFilter, setAssignFilter] = useState<"Assigned" | "Created">("Assigned");
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [dateFilter, setDateFilter] = useState<Nullable<Date>>(today);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");

  /* Add new task */
  const [showTaskModal, setShowTaskModal] = useState<boolean>(false);
  const [taskName, setTaskName] = useState<string>("");
  const [taskDueDate, setTaskDueDate] = useState<Nullable<Date>>(null);
  const [taskDescription, setTaskDescription] = useState<string>("");
  const [taskPriority, setTaskPriority] = useState<Priority | null>(null);
  const [taskProjects, setTaskProjects] = useState<Projects[]>([]);
  const [projectReference, setProjectReference] = useState<string>("");
  const [contacts, setContacts] = useState<AssignContact[]>([]);
  const [taskAssignContacts, setTaskAssignContacts] = useState<AssignContact | null>(null);
  // const [taskAssignContacts, setTaskAssignContacts] = useState<AssignContact[]>(
  //   []
  // );
  const [selectedTask, setSelectedTask] = useState<Tasks | null>(null);
  const [showAddedTaskList, setShowAddedTaskList] = useState<boolean>(false);
  const [selectedAddTask, setSelectedAddTask] = useState<Tasks | null>(null);
  const [taskHr, setTaskHr] = useState<number | null>(0);
  const [taskMin, setTaskMin] = useState<number | null>(0);

  /* Error */
  const [jobNoError, setJobNoError] = useState<string>("");
  const [dueDateError, setDueDateError] = useState<string>("");
  const [priorityError, setPriorityError] = useState<string>("");
  const [assignContactError, setAssignContactError] = useState<string>("");
  const [projectReferenceError, setProjectReferenceError] = useState<string>("");
  const [taskNameError, setTaskNameError] = useState<string>("");
  const [descriptionError, setDescriptionError] = useState<string>("");
  const [taskHrError, setTaskHrError] = useState<string>("");
  const [taskMinError, setTaskMinError] = useState<string>("");
  const [showTaskViewModal, setShowTaskViewModal] = useState<boolean>(false);
  const [selectedTaskData, setSelectedTaskData] = useState<Tasks | null>(null);
  const [createTaskArray, setCreateTaskArray] = useState<CreateTaskArray[]>([]);
  const { loading, employees, professionals } = useAppSelector((state) => state.contact);

  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  const [displayTotalTime, setDisplayTotalTime] = useState<string>("");
  const [addTaskDisable, setAddTaskDisable] = useState<boolean>(false);

  const isPermissionAddTask = useCanEdit("Task", "add_task");
  const isPermissionEditTask = useCanEdit("Task", "edit_task");
  const isPermissionDeleteTask = useCanEdit("Task", "delete_task");
  const isPermissionDetailsView = useCanEdit("Task", "details_view");
  const isPermissionTaskCompleted = useCanEdit("Task", "task_completed");

  //   let statuses: Status[] = [
  //     { label: "Ongoing", value: 0 },
  //     ...(isPermissionTaskCompleted ? [{ label: "Completed", value: 1 }] : []),
  //   ];

  let statuses: Status[] = [
    { label: "Ongoing", value: 0 },
    { label: "Completed", value: 1 },
  ];

  /* **************** Get Data ****************************************************************************************************************************************************** */
  useEffect(() => {
    taskDataGet();
  }, []);

  useEffect(() => {
    dispatch(fetchAllContact());
    dispatch(setStatus(1))
  }, [dispatch]);

  useEffect(() => {
    const employeeList = employees
      .filter((emp) => emp.contactSubType !== "Temporary Employee")
      .map((emp) => {
        const assignContact: AssignContact = {
          id: emp.id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          profile_image: emp.profile_image,
          role: emp.role_name,
          by: "Employee",
        };
        return assignContact;
      });

    const professionalList: AssignContact[] = [];

    for (const emp of professionals) {
      if (emp.contactSubType === ContactSubtype.Company && emp.subContact) {
        for (const sub of emp.subContact) {
          if (sub.employee_type === "Main Employee") {
            const assignContact: AssignContact = {
              id: sub.id,
              first_name: sub.first_name,
              last_name: sub.last_name,
              profile_image: sub.profile_image,
              role: sub.role_name,
              by: "Company",
            };
            professionalList.push(assignContact);
          }
        }
      } else {
        const assignContact: AssignContact = {
          id: emp.id,
          first_name: emp.first_name,
          last_name: emp.last_name,
          profile_image: emp.profile_image,
          role: emp.role_name,
          by: "Self Employee",
        };
        professionalList.push(assignContact);
      }
    }
    setContacts([...employeeList, ...professionalList]);
  }, [employees, professionals]);

  /* task data */
  const taskDataGet = async () => {
    setLoadingPage(true);
    const data = await taskPartGetDataApi();
    if (data.status) {
      setLoadingPage(false);
      setTasksData(data.data || []);
      setFilteredTasksData(data.data || []);
    }
  };

  const handleHrChange = (e: InputNumberChangeEvent) => {
    const newVal = e.value ?? 0;
    setTaskHr(newVal);
  };

  const handleMinChange = (e: InputNumberChangeEvent) => {
    const newVal = e.value ?? 0;
    setTaskMin(newVal);
  };

  /* **************** Get Data ****************************************************************************************************************************************************** */

  /* ************************************* check Credentials ************************************************************************************ */

  useEffect(() => {
    checkText(projectReference, false, setProjectReferenceError);
  }, [projectReference]);

  useEffect(() => {
    const taskDueDateString = formatDate(taskDueDate || null);
    checkText(taskDueDateString, false, setDueDateError);
  }, [taskDueDate]);

  useEffect(() => {
    checkText(taskName, false, setTaskNameError);
  }, [taskName]);

  useEffect(() => {
    checkNumber(Number(taskMin), false, setTaskMinError);
  }, [taskMin, taskHr]);

  function checkCredentials(): boolean {
    const taskDueDateString = formatDate(taskDueDate || null);
    const projectRefe = checkText(
      projectReference,
      true,
      setProjectReferenceError
    );
    const taskDueDateStringValid = checkText(
      taskDueDateString,
      true,
      setDueDateError
    );
    const taskNameValid = checkText(taskName, true, setTaskNameError);
    const taskPriorityValid = checkText(
      taskPriority?.name || "",
      true,
      setPriorityError
    );
    const taskDescriptionValid = checkText(
      taskDescription,
      false,
      setDescriptionError
    );
    const mins =
      checkNumber(Number(taskHr), true, setTaskMinError) ||
      checkNumber(Number(taskMin), true, setTaskMinError);
    return (
      projectRefe &&
      taskDueDateStringValid &&
      taskPriorityValid &&
      taskNameValid &&
      taskDescriptionValid &&
      mins
    );
  }
  /* ************************************* check Credentials ************************************************************************************ */

  /* ************************************* Error massage show ************************************************************************************ */
  function showAPIErrorData(keyName: string, error: string): void {
    if (keyName === "project_program_task_library_id") {
      setTaskNameError(error);
    }
  }

  //   total hrs of task find

  useEffect(() => {
    taskDataGet();
    var hrs: number = 0;
    var mins: number = 0;
    hrs = createTaskArray.reduce((sum, e) => sum + e.hours!, 0);
    mins = createTaskArray.reduce((sum, e) => sum + e.minute!, 0);
    // Convert excess minutes to hours
    hrs += Math.floor(mins / 60);
    mins = mins % 60;
    // Create display string
    const totalTimeString = `${hrs} hrs ${mins} mins`;
    const totalMinutes = hrs * 60 + mins;
    setDisplayTotalTime(totalTimeString);
    if (totalMinutes <= 600) {
      // setDisplayTotalTime(totalTimeString);
      setAddTaskDisable(false);
    } else {
      setAddTaskDisable(true);
    }
  }, [createTaskArray]);
  /* ************************************* Error massage show ************************************************************************************ */

  useEffect(() => {
    setDateFilter(today);
    setSelectedDate(formatDate(today));
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedDate, selectedPriority, selectedProject, tasksData, assignFilter]);

  const handlePriorityChange = (e: DropdownChangeEvent) => {
    const selectedPriority = e.value as Priority;
    setSelectedPriority(selectedPriority?.name || null);
    setPriorityFilter(selectedPriority);
  };

  const handleAssignChange = (e: DropdownChangeEvent) => {
    const selectedFilter = e.value as "Assigned" | "Created";
    setAssignFilter(selectedFilter);
  };

  const handleDateChange = (e: any) => {
    const selectedDate = e.value;
    setDateFilter(selectedDate || null);
    setSelectedDate(formatDate(selectedDate));
  };

  const applyFilters = () => {
    let filteredData = tasksData;

    // if (selectedProject) {
    //     filteredData = filteredData.filter(task => task?.project_id === selectedProject);
    // }

    if (selectedPriority) {
      filteredData = filteredData.filter(
        (task) => task?.priority === selectedPriority
      );
    }

    if (selectedDate) {
      filteredData = filteredData.filter(
        (task) => task?.deu_date === selectedDate
      );
    }

    if (assignFilter) {
      if (assignFilter === "Assigned") {
        filteredData = filteredData.filter(
          (task) => task?.assign_by !== null
        );
      } else if (assignFilter === "Created") {
        filteredData = filteredData.filter(
          (task) => task?.assign_by === null
        );
      }

    }


    setFilteredTasksData(filteredData);
  };

  /* ******************************* For table ******************************* */

  const referenceBodyTemplate = (rowData: Tasks) => {
    return (
      <span className="text_bold">
        <span className="text-capitalize">{rowData.project_reference}</span>
        {/* <small className="table_data_sub text-capitalize">
          {rowData.deu_date}
        </small> */}
      </span>
    );
  };

  const assignedContactBodyTemplate = (rowData: Tasks) => {
    const contacts = rowData?.assign;
    return (
      <React.Fragment>
        {rowData.assign_by !== null ? (
          <React.Fragment>
            <Tooltip target={`#tooltip_assignby_contact_${rowData.task_id}`} />
            <div
              key={rowData.task_id}
              id={`tooltip_assignby_contact_` + rowData.task_id}
              className="task_contact_display m-auto"
              data-pr-tooltip={`Assigned by: ${rowData.assign_by?.first_name + " " + rowData.assign_by?.last_name}`}
              data-pr-position="top"
            >
              {rowData.assign_by?.profile_image !== null ? (
                <img
                  src={`${baseURL + rowData.assign_by?.profile_image}`}
                  alt=""
                />
              ) : (
                <span>
                  {rowData.assign_by.first_name[0]}
                  {rowData.assign_by.last_name[0]}
                </span>
              )}
            </div>
          </React.Fragment>
        ) : loading ? (
          <div className="loading_container">
            <ProgressSpinner
              className="progresscircle custom"
              style={{ width: "50px", height: "50px", stroke: "#8953D7" }}
              strokeWidth="8"
              fill="var(--surface-ground)"
              animationDuration="1s"
            />
          </div>
        ) : contacts?.length > 0 ? (
          <div className="project_card_contact_area justify-content-center mt-0 p-0 no_pointing">
            {contacts.slice(0, 3).map((contact, index) => {
              const contactTooltipId = `tooltip_contact_${rowData.task_id}_${index}`;
              return (
                <React.Fragment>
                  <Tooltip
                    target={`#tooltip_contact_${rowData.task_id}_${index}`}
                  />
                  <div
                    key={contact.id}
                    id={contactTooltipId}
                    className="task_contact_display"
                    data-pr-tooltip={`${contact?.first_name + " " + contact?.last_name
                      }`}
                    data-pr-position="top"
                  >
                    {contact?.profile_image !== null ? (
                      <img
                        src={`${baseURL + contact?.profile_image}`}
                        alt={`${contact.first_name + " " + contact.last_name}`}
                      />
                    ) : (
                      <span>
                        {contact.first_name[0]}
                        {contact.last_name[0]}
                      </span>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {contacts.length > 3 && (
              <React.Fragment>
                <Tooltip target={`.custom-tooltip`} />
                <h6
                  className="project_card_contact_has_more custom-tooltip"
                  data-pr-tooltip={`${contacts.length - 3} more`}
                  data-pr-position="top"
                >
                  +{contacts.length - 3}
                </h6>
              </React.Fragment>
            )}
          </div>
        ) : (
          <span className="text-secondary d-flex justify-content-center">---</span>
        )}
      </React.Fragment>
    );
  };

  const priorityBodyTemplate = (rowData: Tasks) => {
    const priority = rowData.priority;
    let colorCode = "";

    if (priority === "High") {
      colorCode = "#EF9A9A";
    } else if (priority === "Medium") {
      colorCode = "#FFEFBC";
    } else if (priority === "Low") {
      colorCode = "#B9F6CA";
    }

    return (
      <div className="flex_center">
        <span className="priority_label" style={{ backgroundColor: colorCode }}>
          {priority}
        </span>
      </div>
    );
  };

  const getRowClassName = (rowData: Tasks, options: { [key: string]: any }) => {
    const created = rowData?.assign_by === null;

    if (created) {
      return "project_data_row completed";
    } else {
      return "project_data_row";
    }
  };

  /* *********************** Status changed ********************************************************************************************************* */
  const handleStatusChange = (newStatus: number, rowData: Tasks) => {
    confirmDialog({
      message: "Are you sure you want to change the task's status?",
      header: "Confirm Status Change",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      dismissableMask: true,
      resizable: false,
      draggable: false,
      accept: () => {
        rowData.is_completed = newStatus;
        setSelectedStatus(
          statuses.find((opt) => opt.value === newStatus)?.label || ""
        );
        statusChangedTask(rowData.task_id);
      },
      reject: () => {
        setSelectedStatus(
          statuses.find((opt) => opt.value === rowData.is_completed)?.label ||
          ""
        );
      },
    });
  };

  const statusChangedTask = async (taskId: number) => {
    const data = await taskPartStatusChangedApi(taskId);
    if (data.statusCode === 200) {
      taskDataGet();
      // need to tigger
      setTimeout(() => {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Task status changed successfully.",
            life: 3000,
          });
        }
      }, 500);
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error deleting task.",
          detail: "There was an error deleting the task.",
          life: 3000,
        });
      }
    }
  };
  /* *********************** Status changed ********************************************************************************************************* */

  const statusOptionTemplate = (option: Status) => {
    let image = "";

    if (option.value === 1) {
      image = "/assets/images/dashboard/done.svg";
    } else if (option.value === 0) {
      image = "/assets/images/dashboard/in_progress.svg";
    }

    return (
      <div className="status_disply">
        <img src={`${image}`} alt="" />
        <div>{option.label}</div>
      </div>
    );
  };

  const statusValueTemplate = (option: Status, props: DropdownProps) => {
    if (option) {
      let image = "";
      if (option.value === 1) {
        image = "/assets/images/dashboard/done.svg";
      } else if (option.value === 0) {
        image = "/assets/images/dashboard/in_progress.svg";
      }

      return (
        <div className="status_disply">
          <img src={`${image}`} alt="" />
          <div>{option.label}</div>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const statusBodyTemplate = (rowData: Tasks) => {
    return (
      <div className="flex_center">
        <Dropdown
          value={rowData.is_completed}
          options={statuses}
          itemTemplate={statusOptionTemplate}
          valueTemplate={statusValueTemplate}
          onChange={(e) => handleStatusChange(e.value, rowData)}
          panelClassName="table_status_dropdown"
          placeholder="Status"
          className="form_dropdown data_status_dropdown"
          // disabled={rowData.assign_by !== null}
          dropdownIcon={rowData.assign_by !== null}
        />
      </div>
    );
  };

  const tableActionBody = (rowData: Tasks) => {
    return (
      <div className="table_action_btn_area">
        {isPermissionDetailsView && (
          <button
            className="table_action_btn p-ripple"
            onClick={() => handleViewTask(rowData)}
          >
            View
            <Ripple />
          </button>
        )}
        {isPermissionEditTask && rowData.assign_by == null && (
          <button
            className="table_action_btn p-ripple"
            onClick={() => handleEditTask(rowData)}
          >
            Edit
            <Ripple />
          </button>
        )}
      </div>
    );
  };
  /* *************************************************************************** */

  /* ******************************* For task ******************************* */
  const handleClearFields = () => {
    setTaskName("");
    setTaskDescription("");
    setTaskDueDate(null);
    // setTaskPriority(null);
    setTaskAssignContacts(null);
    setTaskHr(0);
    setTaskMin(0);

    // setSelectedTaskProject(null);
  };

  const handleClearFieldsMultiTask = () => {
    setTaskName("");
    setTaskDescription("");
    setTaskHr(0);
    setTaskMin(0);

    // setSelectedTaskProject(null);
  };

  const handleClearError = () => {
    setJobNoError("");
    setDueDateError("");
    setPriorityError("");
    setAssignContactError("");
    setTaskNameError("");
    setDescriptionError("");
  };

  const handleAddNewTask = () => {
    setDataState("Add");

    setShowTaskModal(true);

    handleClearFields();
    handleClearError();
    setTaskPriority(priorities[1]);
  };

  /* ********************************************************** Create ****************************************************** */
  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();

    if (createTaskArray.length > 0) {
      postTaskList(createTaskArray);
    } else {
      const taskDueDateString = formatDate(taskDueDate || null);
      const assignContact: AssignContact[] = [];
      if (taskAssignContacts !== null) {
        assignContact[0] = taskAssignContacts;
      }

      if (checkCredentials()) {
        var makeArray: CreateTaskArray = {
          created_token: `token-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          task_name: taskName,
          description: taskDescription,
          deu_date: taskDueDateString,
          priority: taskPriority?.name,
          hours: taskHr!,
          minute: taskMin!,
          assign: assignContact,
        };

        const taskArray: CreateTaskArray[] = [];
        taskArray.push(makeArray);

        postTaskList(taskArray);
      }
    }
  };

  const postTaskList = async (createTaskArray: CreateTaskArray[]) => {
    if (createTaskArray.length > 0) {
      setIsLoading(true);
      const data = await taskPartCreateApi(projectReference, createTaskArray);
      setIsLoading(false);

      if (data.statusCode === 200) {
        taskDataGet();
        handleConfirmCloseTaskModal();
        setTimeout(() => {
          setSuccessMessage("Task created successfully.");
          setShowAlert(true);
          setTimeout(() => {
            setSuccessMessage("");
            setShowAlert(false);
          }, 2000);
        }, 500);

        setCreateTaskArray([]);
        setShowAddedTaskList(false);
        handleClearFields();
        setProjectReference("");
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value], index) =>
          showAPIErrorData(key, value[0])
        );
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error creating task.",
            detail: "There was an error creating the task.",
            life: 3000,
          });
        }
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error creating task.",
          detail: "Please add task before save.",
          life: 3000,
        });
      }
    }
  };
  /* ********************************************************** Create ****************************************************** */

  /* ********************************************************** Add multiple task array ****************************************************** */
  const handleAddTask = () => {
    const taskDueDateString = formatDate(taskDueDate || null);
    if (checkCredentials()) {
      const assignlist: AssignContact[] = [];
      if (taskAssignContacts !== null) {
        assignlist[0] = taskAssignContacts;
      }

      var makeArray: CreateTaskArray = {
        created_token: `token-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        task_name: taskName,
        description: taskDescription,
        deu_date: taskDueDateString,
        priority: taskPriority?.name,
        hours: taskHr!,
        minute: taskMin!,
        assign: assignlist,
      };
      setCreateTaskArray([...createTaskArray, makeArray]);
      setShowAddedTaskList(true);
      handleClearFieldsMultiTask();
    }
  };
  /* ********************************************************** Add multiple task array ****************************************************** */

  const handleSaveChanges = () => { };

  const validateFields = () => { };

  const handleDeleteAddedTask = (indexToRemove: number) => {
    setCreateTaskArray((prevArray) => {
      const updatedArray = prevArray.filter(
        (_, index) => index !== indexToRemove
      );

      if (updatedArray.length === 0) {
        setShowAddedTaskList(false);
      }

      return updatedArray;
    });
  };

  /* ********************************************************** Update ****************************************************** */

  const handleEditTask = (data: Tasks) => {
    setShowTaskModal(true);
    setDataState("Edit");
    setShowAddedTaskList(false);
    setSelectedTask(data);

    setTaskDueDate(parseStringToDate(data?.deu_date || ""));
    // setSelectedTaskProject(projectsData.filter(project => project?.project_id === data?.project_id)[0]);

    setProjectReference(data?.project_reference || "");
    setTaskHr(data?.hours!);
    setTaskMin(data.minute!);
    setTaskPriority(
      priorities.filter((priority) => priority.name === data?.priority)[0]
    );

    const assignedContact = contacts.filter((con) =>
      data.assign.some((assigned) => assigned.id === con.id)
    );

    setTaskAssignContacts(assignedContact[0] || null);
    setTaskName(data?.task_name);
    setTaskDescription(data?.description || "");
  };

  const handleUpdateTask = async (e: React.FormEvent) => {
    e.preventDefault();

    const taskDueDateString = formatDate(taskDueDate || null);
    if (checkCredentials()) {
      const assignlist: AssignContact[] = [];
      if (taskAssignContacts !== null) {
        assignlist[0] = taskAssignContacts;
      }

      setIsLoading(true);
      const data = await taskPartUpdateApi(
        selectedTask?.task_id || null,
        projectReference || null,
        taskName,
        taskHr!,
        taskMin!,
        taskDescription,
        taskDueDateString,
        taskPriority?.name || "",
        assignlist
      );
      setIsLoading(false);

      if (data.statusCode === 200) {
        taskDataGet();
        handleCloseTaskModal();
        setTimeout(() => {
          setSuccessMessage("Task updated successfully.");
          setShowAlert(true);
          setTimeout(() => {
            setSuccessMessage("");
            setShowAlert(false);
          }, 2000);
        }, 500);

        setCreateTaskArray([]);
        setShowAddedTaskList(false);
        handleClearFields();
        setProjectReference("");
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value], index) =>
          showAPIErrorData(key, value[0])
        );
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error creating task.",
            detail: "There was an error creating the task.",
            life: 3000,
          });
        }
      }
    }
  };

  /* ********************************************************** Update ****************************************************** */

  const handleCloseTaskModal = () => {
    if (createTaskArray.length > 0) {
      confirmDialog({
        message: `When you close the add task, the tasks already added will be deleted, are you sure you want to exit?`,
        header: "Are you sure you want to exit?",
        icon: "bi bi-info-circle",
        defaultFocus: "accept",
        acceptClassName: "p-button-danger",
        accept: () => handleConfirmCloseTaskModal(),
      });
    } else {
      handleConfirmCloseTaskModal();
    }
  };

  const handleConfirmCloseTaskModal = () => {
    setShowTaskModal(false);
    setCreateTaskArray([]);
    setProjectReference("");
  };

  /* Modal header */
  const taskModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">{dataState}&nbsp;task</h5>
      {dataState === "Edit" ? (
        isPermissionDeleteTask ? (
          <button
            type="button"
            className="close_modal_btn p-ripple"
            onClick={() => handleDeleteTask()}
          >
            <i className="bi bi-trash3"></i>
            <Ripple />
          </button>
        ) : null
      ) : (
        <button
          type="button"
          className="close_modal_btn p-ripple"
          onClick={handleCloseTaskModal}
        >
          <i className="bi bi-x-circle"></i>
          <Ripple />
        </button>
      )}
    </div>
  );

  /* Modal footer */
  const taskModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseTaskModal}
      />
      <Button
        label={
          isLoading ? "Processing" : dataState === "Add" ? "Save" : "Update"
        }
        onClick={dataState === "Add" ? handleCreateTask : handleUpdateTask}
        loading={isLoading}
        className="custom_btn primary"
      />
    </div>
  );

  const jobNoOptionTemplate = (option: Projects) => {
    return (
      <div
        className={`address_option_container ${option?.project_type === "CP" ? "bg-blue" : "bg-purple"
          }`}
      >
        <span>
          <b>{option?.job_number}</b>
        </span>
        ,&nbsp;
        <br />
        <small>{option?.address}</small>
      </div>
    );
  };

  const selectedJobNoTemplate = (option: Projects, props: DropdownProps) => {
    if (option) {
      return (
        <div className="d-flex align-items-center overflow-hidden">
          <div>
            <b>{option?.job_number}</b>&nbsp;-&nbsp;{option?.address}
          </div>
        </div>
      );
    }

    return <span>{props.placeholder}</span>;
  };

  const handleJobNoChange = (e: DropdownChangeEvent) => {
    const selectedOption = e.value as Projects | null;
    // setSelectedTaskProject(selectedOption);
  };

  /* ********************************************************** Delete ****************************************************** */
  const handleDeleteTask = (): void => {
    confirmDialog({
      message: `Are you sure you want to delete the task detail?`,
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteTask(),
    });
  };

  const deleteTask = async () => {
    const data = await taskPartDeleteApi(selectedTask?.task_id || null);

    if (data.statusCode === 200) {
      taskDataGet();
      setShowTaskModal(false);
      setTimeout(() => {
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Task detail deleted successfully.",
            life: 3000,
          });
        }
      }, 500);
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error deleting task.",
          detail: "There was an error deleting the task.",
          life: 3000,
        });
      }
    }
  };
  /* ********************************************************** Delete ****************************************************** */

  /* For view task */
  const handleViewTask = (data: Tasks) => {
    setShowTaskViewModal(true);
    setSelectedTaskData(data);
  };

  const handleCloseTaskViewModal = () => {
    setShowTaskViewModal(false);
  };

  /* Modal header */
  const taskViewModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Task details</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseTaskViewModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const viewTaskPriority = (priority: string) => {
    let colorCode = "";

    if (priority === "High") {
      colorCode = "#EF9A9A";
    } else if (priority === "Medium") {
      colorCode = "#FFEFBC";
    } else if (priority === "Low") {
      colorCode = "#B9F6CA";
    }

    return (
      <span className="priority_label" style={{ backgroundColor: colorCode }}>
        {priority}
      </span>
    );
  };

  const viewTaskStatus = (isCompleted: number) => {
    let image = "";
    let status = "";

    if (isCompleted === 1) {
      status = "Completed";
      image = "/assets/images/dashboard/done.svg";
    } else if (isCompleted === 0) {
      status = "Ongoing";
      image = "/assets/images/dashboard/in_progress.svg";
    }

    return (
      <div className="status_disply">
        <img src={image} alt={status} />
        <span>{status}</span>
      </div>
    );
  };

  const viewTaskDueDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  /* *************************************************************************** */

  const contactOptionsTemplate = (option: AssignContact) => {
    return (
      <div className="contact_option_group">
        <div className="contact_profile">
          {option?.profile_image ? (
            <img
              src={baseURL + option.profile_image}
              alt={option?.first_name + " " + option?.last_name}
            />
          ) : (
            <span>{option?.first_name[0] + option?.last_name[0]}</span>
          )}
        </div>
        <div className="contact_details">
          <h6>
            {option?.first_name || option?.last_name
              ? `${option?.first_name ?? ""} ${option?.last_name ?? ""}`.trim()
              : ""}
          </h6>
          <small>{option?.role}</small>
        </div>
      </div>
    );
  };

  /* ******************************* For priority ******************************* */
  const priorityOptionTemplate = (option: Priority) => {
    return (
      <div className="d-flex align-items-center w-100 gap-4 justify-content-between">
        <div>{option.name}</div>
        <div
          className="color_display"
          style={{ backgroundColor: option.colorCode }}
        ></div>
      </div>
    );
  };

  const taskOptionTemplate = (option: string) => {
    return (
      <div className="d-flex align-items-center w-100 gap-4 justify-content-between">
        <div>{option}</div>
      </div>
    );
  };
  /* *************************************************************************** */

  /* Common function */
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const goBack = () => {
    navigate(-1);
    goToTop();
  };

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="main_heading">Task</h4>
              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area has_multi_dropdown mt-sm-3 mt-3">
              <div className="filter_option_group">
                <div className="filter_date_input_group">
                  <Calendar
                    id="date"
                    value={dateFilter}
                    onChange={handleDateChange}
                    dateFormat="dd M yy"
                    // maxDate={today}
                    placeholder="Select date"
                    className="custom_date_input filter_date_input custom_width"
                    showIcon
                    iconPos="left"
                    icon="bi bi-calendar2-minus-fill"
                    readOnlyInput
                  />
                  {dateFilter && (
                    <i
                      className="bi bi-x date_clear_icon"
                      onClick={() => {
                        setDateFilter(null);
                        setSelectedDate("");
                      }}
                    ></i>
                  )}
                </div>

                <div className="filter_dropdown_group">
                  {/* <Dropdown
                    id="jobNo"
                    value={projectFilter}
                    onChange={handleProjectChange}
                    options={projectsData}
                    optionLabel="job_number"
                    panelClassName="status_option_panel"
                    placeholder="Job no."
                    className="form_dropdown filter_dropdown"
                    filter
                    filterPlaceholder="Search here"
                    showClear
                  /> */}

                  <Dropdown
                    id="priority"
                    value={priorityFilter}
                    onChange={handlePriorityChange}
                    options={priorities}
                    optionLabel="name"
                    itemTemplate={priorityOptionTemplate}
                    panelClassName="status_option_panel"
                    placeholder="Priority"
                    className="form_dropdown filter_dropdown"
                    showClear
                  />

                  <Dropdown
                    id="task"
                    value={assignFilter}
                    onChange={handleAssignChange}
                    options={["Assigned", "Created"]}
                    panelClassName="status_option_panel"
                    placeholder="Task"
                    className="form_dropdown filter_dropdown"

                  />
                </div>
              </div>
              {isPermissionAddTask && (
                <div className="page_action_area">
                  <button
                    className="add_data_link_btn p-ripple"
                    onClick={handleAddNewTask}
                  >
                    <i className="bi bi-plus-circle"></i>
                    <span>Add new task</span>
                    <Ripple />
                  </button>
                </div>
              )}
            </div>

            <div className="page_main_content_section">
              {tasksData?.length > 0 ? (
                <DataTable
                  value={filteredTasksData}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No task(s) found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  rowHover
                  scrollable
                >
                  <Column
                    header="Task name"
                    headerClassName="text_purple"
                    field="task_name"
                    body={(rowData: Tasks) => (
                      <span className="text-capitalize text_bold">
                        {rowData?.task_name ? rowData?.task_name : "---"}
                      </span>
                    )}
                    style={{ width: "25%" }}
                  ></Column>

                  <Column
                    header="Reference"
                    headerClassName="text_purple"
                    field="project_reference"
                    body={(rowData: Tasks) => (
                      <span className="text-capitalize">
                        {rowData?.project_reference ? rowData?.project_reference : "---"}
                      </span>
                    )}
                    style={{ width: "22%" }}
                  ></Column>


                  <Column
                    header="Due date"
                    headerClassName="text_purple"
                    field="deu_date"
                    body={(rowData: Tasks) => (
                      <span className="text_no_wrap">
                        {rowData?.deu_date ? rowData?.deu_date : "---"}
                      </span>
                    )}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    header="Assign"
                    alignHeader={'center'}
                    headerClassName="text_purple"
                    field="assign_by"
                    body={assignedContactBodyTemplate}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    header="Priority"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="priority"
                    body={priorityBodyTemplate}
                    style={{ width: "8%" }}
                  ></Column>

                  <Column
                    header="Status"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="is_completed"
                    body={statusBodyTemplate}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    alignHeader="center"
                    body={tableActionBody}
                    className="table_btn_area"
                    frozen
                    alignFrozen="right"
                  ></Column>
                </DataTable>
              ) : (
                <NoDataCreated message={`No tasks have been added yet`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Task add/edit modal  */}
      <Dialog
        visible={showTaskModal}
        header={taskModalHeader}
        footer={taskModalFooter}
        headerClassName="custom_modal_header"
        className={`custom_modal_dialog ${showAddedTaskList ? "modal_dialog_lg" : "modal_dialog_md"}`}
        contentClassName={`${dataState === "Add" && "pb-sm-0"}`}
        onHide={handleCloseTaskModal}
        dismissableMask
      >
        <div className="custom_modal_body pb-sm-0">
          <div className={`p-1 ps-sm-3 pe-sm-3 w-100 ${dataState === "Add" && "pb-sm-0"}`}>
            <div className="row flex_col_reverse">
              <div className={`col-12 ${showAddedTaskList && "col-lg-8 pe-lg-4"}`}>
                <div className={`row ${showAddedTaskList && "pt-3 pt-sm-4 pt-lg-0"}`}>
                  <div className="col-12">
                    <TextInput
                      id="projectReference"
                      label="Project Reference"
                      labelHtmlFor="projectReference"
                      required={true}
                      value={projectReference}
                      placeholder="Project Reference"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectReference(e.target.value)}
                      error={projectReferenceError}
                    />
                  </div>

                  {/* date */}
                  <div className={`col-12 col-md-4 col-sm-3`}>
                    <DatePicker
                      id="dueDate"
                      label="Due date"
                      labelHtmlFor="dueDate"
                      required={true}
                      value={taskDueDate}
                      placeholder="dd/mm/yyyy"
                      onChange={setTaskDueDate}
                      error={dueDateError}
                      showClear
                      onClear={() => setTaskDueDate(null)}
                    />
                  </div>

                  <div className={`col-12 col-md-8 col-sm-9 ${showAddedTaskList && "col-md-8 col-sm-9"}`}>
                    <div className="page_form_group">
                      <div className="w-100">
                        <label
                          htmlFor="assignedContact"
                          className="custom_form_label"
                        >
                          Assign
                        </label>

                        <Dropdown
                          id="priority"
                          value={taskAssignContacts}
                          onChange={(e: DropdownChangeEvent) => setTaskAssignContacts(e.value)}
                          options={contacts}
                          optionLabel="first_name"
                          itemTemplate={contactOptionsTemplate}
                          // valueTemplate={}
                          placeholder="Assign"
                          filter
                          filterInputAutoFocus={true}
                          filterPlaceholder="Search by name.."
                          filterBy="first_name,last_name"
                          className="form_dropdown w-100"
                          emptyFilterMessage="No contact(s) found!"
                          showClear
                        />

                        {/* back up for multi select. */}
                                                 
                        <MultiSelect
                          value={taskAssignContacts}
                          onChange={(e: MultiSelectChangeEvent) =>
                            setTaskAssignContacts(e.value)
                          }
                          options={contacts}
                          display="chip"
                          optionLabel="first_name"
                          showClear
                          itemTemplate={contactOptionsTemplate}
                          placeholder="Assign"
                          filter
                          filterInputAutoFocus={true}
                          filterPlaceholder="Search by name.."
                          maxSelectedLabels={4}
                          filterBy="first_name,last_name"
                          className="w-100"
                          maxLength={1}
                          emptyFilterMessage="No contact(s) found!"
                        /> 

                        {/* <MultiSelect value={serviceType} onChange={(e: MultiSelectChangeEvent) => setServiceType(e.value)}
                                                        options={allServiceType} display="chip" optionLabel="name" showClear
                                                        placeholder="Service type" filter filterInputAutoFocus={true} filterPlaceholder="Search service type.."
                                                        maxSelectedLabels={4} className="w-100" emptyFilterMessage="No service type(s) found!" /> */}

                        {assignContactError && (
                          <small className="form_error_msg">
                            {assignContactError}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* task name */}
                  <div className="col-12">
                    <TextInput
                      id="taskName"
                      label="Task name"
                      labelHtmlFor="taskName"
                      required={true}
                      value={taskName}
                      placeholder="Task name"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTaskName(e.target.value)}
                      error={taskNameError}
                    />
                  </div>

                  {/* time string Hr */}
                  <div className="col-12 col-sm-3">
                    <NumberInput
                      key={`hr`}
                      inputId="hr"
                      label="Hr"
                      labelHtmlFor="hr"
                      placeholder="Enter Hr"
                      onChange={handleHrChange}
                      value={taskHr}
                      required={true}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      min={0}
                      max={24}
                      error={taskHrError}
                    />
                  </div>

                  {/* task min */}
                  <div className="col-12 col-sm-3">
                    <NumberInput
                      key={`min`}
                      inputId="min"
                      label="Min"
                      labelHtmlFor="min"
                      placeholder="Enter Min"
                      onChange={handleMinChange}
                      value={taskMin}
                      required={true}
                      minFractionDigits={0}
                      maxFractionDigits={0}
                      min={0}
                      max={60}
                      error={taskMinError}
                    />
                  </div>

                  <div className={`col-12 col-sm-6 col-md-6`}>
                    <div className="page_form_group">
                      <label
                        htmlFor="priority"
                        className="custom_form_label is_required"
                      >
                        Priority
                      </label>
                      <Dropdown
                        id="priority"
                        value={taskPriority}
                        onChange={(e: DropdownChangeEvent) => setTaskPriority(e.value)}
                        options={priorities}
                        optionLabel="name"
                        itemTemplate={priorityOptionTemplate}
                        placeholder="Select"
                        className="form_dropdown w-100"
                        showClear
                      />

                      {priorityError && (
                        <small className="form_error_msg">
                          {priorityError}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-12">
                    <Textarea
                      id="taskDescription"
                      label="Description/note"
                      labelHtmlFor="taskDescription"
                      required={false}
                      value={taskDescription}
                      placeholder="Description"
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTaskDescription(e.target.value)}
                      error={descriptionError}
                      formGroupClassName="mb-0"
                    />
                  </div>
                </div>

                {dataState === "Add" && (
                  <React.Fragment>
                    <div className="flex_end w-100 mt-4 add_task_btn_area">
                      <button
                        className={`add_data_btn p-ripple`}
                        onClick={handleAddTask}
                      >
                        <i className={`bi bi-plus-circle`}></i>
                        Add
                        <Ripple />
                      </button>
                    </div>
                    {/* <div className="flex_end w-100 mt-4 add_task_btn_area">
                                            <button className={`add_data_btn p-ripple`} onClick={handleSaveChanges}>
                                                <i className={`bi bi-check-circle`}></i>
                                                Save changes
                                                <Ripple />
                                            </button>
                                        </div> */}
                  </React.Fragment>
                )}
              </div>

              {showAddedTaskList && (
                <div className="col-12 col-lg-4 ps-lg-4 add_list_divider">
                  <div className="total_time_area">
                    <h6>Total duration :</h6>
                    <span>{displayTotalTime}</span>
                  </div>

                  <div className="added_task_list pb-2 pb-sm-3">
                    {createTaskArray.map((item, index) => (
                      <div className="added_task_list_item has_sub">
                        <div className="added_task_list_sub">
                          <button className="added_task is_btn p-ripple text-capitalize">
                            <span>{index + 1}.</span>&nbsp; {item.task_name}
                            <Ripple />
                          </button>
                          <button
                            type="button"
                            className="close_modal_btn small d-flex align-items-center danger p-ripple"
                            onClick={() => handleDeleteAddedTask(index)}
                          >
                            <i className="bi bi-trash3 icon_sm"></i>
                            <Ripple />
                          </button>
                        </div>

                        <div className="task_duration">
                          <small>
                            Duration : {item.hours} Hour(s) {item.minute}{" "}
                            Minute(s)
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Task view modal */}
      <Dialog
        visible={showTaskViewModal}
        header={taskViewModalHeader}
        headerClassName="custom_modal_header"
        className={`custom_modal_dialog modal_dialog_md`}
        onHide={handleCloseTaskViewModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="p-1">
            <div className="row">
              <div className="col-12">
                <h6 className="data_view_title text-capitalize">
                  {selectedTaskData?.task_name}
                </h6>
                <div className="data_view_group mt-2">
                  <h6 className="data_view_title text_gray text-capitalize">
                    {selectedTaskData?.project_reference}
                  </h6>
                  <span>|</span>
                  <p className="data_view_detail">
                    Due date:{" "}
                    {selectedTaskData?.deu_date ? viewTaskDueDate(selectedTaskData.deu_date) : ""}
                  </p>
                </div>
              </div>
            </div>

            <hr className="custom_horizontal_line" />

            <div className="row">
              <div className="col-12">
                <h6 className="data_view_title">Description</h6>
                <p className="data_view_description mt-2">
                  {selectedTaskData?.description ? selectedTaskData?.description : "----"}
                </p>
              </div>
            </div>

            <hr className="custom_horizontal_line" />

            <div className="row">
              <div className="col-3">
                <h6 className="data_view_title">Priority</h6>
              </div>
              <div className="col-9">
                {viewTaskPriority(selectedTaskData?.priority!)}
              </div>
            </div>

            <hr className="custom_horizontal_line" />

            {selectedTaskData?.assign !== null && (
              <div className="row">
                <div className="col-3">
                  <h6 className="data_view_title">Assign</h6>
                </div>
                <div className="col-9">
                  <div className="project_card_contact_area mt-0 p-0 no_pointing">
                    {selectedTaskData?.assign !== null && selectedTaskData?.assign.length !== 0 ? (
                      <React.Fragment>
                        {selectedTaskData?.assign &&
                          selectedTaskData?.assign.map((contact, index) => {
                            const contactTooltipId = `contact_tooltip_${contact.id}_${index}`;
                            return (
                              <React.Fragment>
                                <Tooltip
                                  target={`#contact_tooltip_${contact.id}_${index}`}
                                />
                                <div
                                  key={contact.id}
                                  id={contactTooltipId}
                                  className="contact_profile"
                                  data-pr-tooltip={`${contact?.first_name + " " + contact?.last_name}`}
                                  data-pr-position="top"
                                >
                                  {contact?.profile_image !== "" ? (
                                    <img
                                      src={`${baseURL + contact?.profile_image}`}
                                      alt={`${contact.first_name + " " + contact.last_name}`}
                                    />
                                  ) : (
                                    <span>
                                      {contact.first_name[0]}
                                      {contact.last_name[0]}
                                    </span>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                      </React.Fragment>
                    ) : (
                      <span>----</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedTaskData?.assign_by !== null && (
              <div className="row">
                <div className="col-3">
                  <h6 className="data_view_title">Assigned by</h6>
                </div>
                <div className="col-9">
                  <div className="project_card_contact_area mt-0 p-0 no_pointing">
                    <Tooltip
                      target={`#contact_assign_tooltip`}
                    />

                    <div
                      id={`contact_assign_tooltip`}
                      className="contact_profile"
                      data-pr-tooltip={`${selectedTaskData?.assign_by?.first_name + " " + selectedTaskData?.assign_by?.last_name}`}
                      data-pr-position="top"
                    >
                      {selectedTaskData?.assign_by?.profile_image !== null ? (
                        <img
                          src={`${baseURL + selectedTaskData?.assign_by?.profile_image}`}
                          alt={`${selectedTaskData?.assign_by?.first_name + " " + selectedTaskData?.assign_by?.last_name}`}
                        />
                      ) : (
                        <span>
                          {selectedTaskData?.assign_by.first_name[0]}
                          {selectedTaskData?.assign_by.last_name[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <hr className="custom_horizontal_line" />

            <div className="row">
              <div className="col-3">
                <h6 className="data_view_title">Status</h6>
              </div>
              <div className="col-9">
                {viewTaskStatus(selectedTaskData?.is_completed!)}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Show success message */}
      <div
        className={`message_backdrop ${showAlert ? "show" : ""}`}
        onClick={handleCloseAlert}
      >
        <div
          className={`message_box ${showAlert ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="message_box_content">
            <i className="bi bi-check-circle-fill message_box_icon"></i>
            <h6 className="message_box_message">{successMessage}</h6>
          </div>
        </div>
      </div>
      {/*  */}
    </React.Fragment>
  );
};

export default Task;
